import type { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'white' | 'gold' | 'green' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

// «Granito & Ouro» buttons: squared (rounded-md), solid fills or bordered
// surfaces, colour-only hover. Mirrors components/public/ui/Button.
const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark',
  secondary:
    'bg-surface border border-line-strong text-on-surface hover:border-primary hover:text-primary',
  ghost:
    'bg-transparent border border-line-strong text-on-surface hover:border-primary hover:text-primary',
  // `white` existed for dark backdrops (now gone); alias to the bordered surface.
  white:
    'bg-surface border border-line-strong text-on-surface hover:border-primary hover:text-primary',
  gold: 'bg-secondary text-white hover:bg-secondary-dark',
  green: 'bg-tertiary text-white hover:bg-tertiary-dark',
  danger: 'bg-error text-white hover:bg-error/90',
};

const SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'text-sm px-4 py-2',
  md: 'text-base px-6 py-3',
  lg: 'text-lg px-8 py-4',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  disabled,
  loading = false,
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const disabledClass = isDisabled ? 'opacity-60 cursor-not-allowed' : '';

  return (
    <button
      type={type}
      disabled={isDisabled}
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors duration-200 ${VARIANTS[variant]} ${SIZES[size]} ${disabledClass} ${className}`}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-white/45 border-t-white"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
}
